import { autoUpdate, flip, offset, size, useClick, useDismiss, useFloating, useInteractions } from '@floating-ui/react'
import { Combobox } from '@headlessui/react'
import classNames from 'classnames/dedupe'
import { Fragment, MutableRefObject, useEffect, useRef, useState } from 'react'
import { GroupedComboOptions } from 'app/components/shared/GroupedComboOptions'
import { ComboButton } from 'app/components/shared/styled/ComboButton'
import { ComboOptions } from 'app/components/shared/styled/ComboOptions'
import { Tooltip } from 'app/components/Tooltip'
import { debug } from 'lib/logging'
import { OptionType } from 'types/OptionType'

export type ComboboxProps = {
  name?: string
  options?: OptionType[]
  placeholder?: string
  className?: string
  inputClassName?: string
  optionsContainerClassName?: string
  optionClassName?: string
  chevronClassName?: string
  customIcon?: React.ReactNode
  customButton?: React.ReactNode
  customLabel?: string
  defaultValue?: OptionType
  value?: OptionType
  onChange?: (val: OptionType) => void
  onChangeMultiple?: (val: OptionType[]) => void
  onSearchValueChange?: (text: string) => void
  onClearSearch?: () => void
  onButtonClick?: () => void
  loading?: boolean
  open?: boolean
  showSearchInDropdown?: boolean
  disabled?: boolean
  multiple?: boolean
  enableDeselectAllOption?: boolean
  deselectAllOptions?: OptionType[]
  selected?: OptionType[]
  onAddNewValue?: (val: string) => void
  forceOpen?: boolean
  checkboxSide?: `left` | `right`
  showButtonTooltip?: boolean
  allowDeselectOption?: boolean
  useSingleFieldInput?: boolean
  isFilter?: boolean
  placement?: `bottom-start` | `bottom-end` | `top-start` | `top-end`
  addText?: string
  isOptionsGrouped?: boolean
}

export default function ComboBoxComponent({
  name,
  options,
  placeholder,
  defaultValue,
  value,
  className,
  inputClassName,
  optionsContainerClassName,
  optionClassName,
  chevronClassName,
  onSearchValueChange,
  customIcon,
  customButton,
  customLabel,
  loading = false,
  open = false,
  showSearchInDropdown = false,
  disabled = false,
  onChange = () => {},
  onClearSearch,
  onButtonClick,
  multiple = false,
  enableDeselectAllOption = false,
  deselectAllOptions,
  selected,
  onChangeMultiple,
  onAddNewValue,
  forceOpen,
  checkboxSide,
  showButtonTooltip = false,
  allowDeselectOption,
  useSingleFieldInput = true,
  isFilter,
  placement = `bottom-start`,
  addText = `Add new value`,
  isOptionsGrouped = false,
}: ComboboxProps) {
  // Detect mobile and adjust behavior
  const isMobile = typeof window !== `undefined` && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
  const [button, setButton] = useState<HTMLButtonElement | null>(null)
  const comboDropdownInputRef = useRef() as MutableRefObject<HTMLInputElement>
  const [tooltipContent, setTooltipContent] = useState<string | undefined>(undefined)
  const [isOpen, setOpen] = useState(open)
  let [popperElement, setPopperElement] = useState<HTMLUListElement | null>(null)
  const handleOpenChange = (open: boolean) => {
    setOpen(open)
    if (open) {
      onButtonClick?.()
      // On mobile, scroll the button to the top of the viewport when opening
      if (isMobile && button) {
        const buttonRect = button.getBoundingClientRect()
        const scrollTop = window.scrollY || document.documentElement.scrollTop
        const targetScrollPosition = buttonRect.top + scrollTop - 10 // 10px padding from top

        window.scrollTo({
          top: targetScrollPosition,
          behavior: `smooth`,
        })
      }
    }
  }

  const handleSingleValueChange = (val: OptionType) => {
    onChange(val)
    setOpen(false)
    onClearSearch?.()
  }

  let floatingData = useFloating({
    placement,
    middleware: [
      offset(5),
      flip({
        fallbackPlacements: [`bottom-start`, `bottom-end`, `top-start`, `top-end`],
      }),
      size({
        apply({ rects, elements, availableHeight, availableWidth }) {
          Object.assign(elements.floating.style, {
            maxHeight: `${availableHeight}px`,
            minWidth: `${rects.reference.width}px`,
            maxWidth: availableWidth > 620 ? `620px` : `${availableWidth}px`,
          })
        },
        padding: 10,
      }),
    ],
    elements: { reference: button, floating: popperElement },
    open: isOpen,
    onOpenChange: handleOpenChange,
    whileElementsMounted: autoUpdate,
  })

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useClick(floatingData.context),
    useDismiss(floatingData.context, {
      // On mobile, don't dismiss on scroll to prevent closing when keyboard appears
      ancestorScroll: !isMobile,
    }),
  ])

  useEffect(() => {
    // if the caller wants it open, then click the button on render to open it
    if (open) button?.click()
  }, [button, open])

  useEffect(() => {
    debug(`contacts`, `ComboBoxComponent`, name, { value })
  }, [value])

  return (
    <>
      {!multiple ? (
        <Combobox
          as={Fragment}
          defaultValue={defaultValue}
          name={name}
          value={value}
          onChange={handleSingleValueChange}
          disabled={disabled}
        >
          <>
            {useSingleFieldInput && (
              <input
                x-component-name="combo-box-component-input-hidden"
                type="hidden"
                name={name}
                value={value?.value}
              />
            )}
            <div
              className={classNames(className, `relative h-full`, disabled && `cursor-not-allowed`, isOpen && `z-50`)}
            >
              {customButton ? (
                <Combobox.Button
                  as="div"
                  className="bg-white"
                  data-testid="combo-button-1"
                  ref={setButton}
                  onClick={() => {
                    onButtonClick?.()
                  }}
                  {...getReferenceProps()}
                >
                  {customButton}
                </Combobox.Button>
              ) : (
                <ComboButton
                  {...{
                    inputClassName,
                    setButton,
                    onButtonClick,
                    customIcon,
                    chevronClassName,
                    disabled,
                    referenceProps: getReferenceProps(),
                  }}
                >
                  <Tooltip
                    placement="top"
                    className={classNames(
                      `truncate pr-2`,
                      disabled ? `!cursor-not-allowed text-trn-300 opacity-70` : `!cursor-pointer`,
                      { 'text-body-sm text-trn-900': !inputClassName }, // This allows to set custom text color and sizes
                    )}
                    message={showButtonTooltip ? tooltipContent : undefined}
                    onMouseOver={(event) => {
                      if (!showButtonTooltip) return
                      if (
                        event.currentTarget instanceof HTMLElement &&
                        event.currentTarget.scrollWidth > event.currentTarget.clientWidth
                      ) {
                        setTooltipContent(value?.name)
                      } else {
                        setTooltipContent(undefined)
                      }
                    }}
                  >
                    <span
                      data-testid="combo-value"
                      className={classNames(
                        !value?.name ? `text-neutral-400` : `text-trn-900`,
                        disabled ? `cursor-not-allowed text-trn-300 opacity-70` : `cursor-pointer`,
                      )}
                    >
                      {value?.name ?? placeholder ?? `Search`}
                    </span>
                  </Tooltip>
                </ComboButton>
              )}
              {isOptionsGrouped ? (
                // For TRK-3644 - It shows options in grouped format
                // As of now, it seams unlikely to be multiple
                <GroupedComboOptions
                  {...{
                    open: isOpen,
                    floatingStyles: floatingData.floatingStyles,
                    options,
                    onClearSearch,
                    comboDropdownInputRef,
                    setPopperElement,
                    showSearchInDropdown,
                    value,
                    floatingProps: getFloatingProps(),
                  }}
                />
              ) : (
                <ComboOptions
                  {...{
                    open: isOpen,
                    forceOpen,
                    optionsContainerClassName,
                    setPopperElement,
                    floatingStyles: floatingData.floatingStyles,
                    floatingProps: getFloatingProps(),
                    showSearchInDropdown,
                    comboDropdownInputRef,
                    placeholder,
                    onSearchValueChange,
                    onClearSearch,
                    loading,
                    options,
                    optionClassName,
                    onAddNewValue,
                    value,
                    allowDeselectOption: allowDeselectOption,
                    addText,
                  }}
                />
              )}
            </div>
          </>
        </Combobox>
      ) : (
        <Combobox
          as={Fragment}
          name={name}
          value={selected}
          onChange={onChangeMultiple}
          disabled={disabled}
          by="id"
          multiple
        >
          <div
            className={classNames(
              className,
              `relative`,
              disabled && `cursor-not-allowed text-trn-300 opacity-70`,
              isOpen && `z-50`,
            )}
          >
            {customButton ? (
              <Combobox.Button
                as="div"
                className="bg-white"
                data-testid="combo-button-1"
                ref={setButton}
                onClick={() => {
                  onButtonClick?.()
                }}
                {...getReferenceProps()}
              >
                {customButton}
              </Combobox.Button>
            ) : (
              <ComboButton
                {...{
                  inputClassName,
                  setButton,
                  onButtonClick,
                  customIcon,
                  chevronClassName: classNames(chevronClassName, disabled && `d-none`),
                  referenceProps: getReferenceProps(),
                }}
              >
                <Tooltip
                  placement="top"
                  message={selected && selected.length > 1 ? selected?.map((opt) => opt.name).join(`, `) : undefined}
                  className={classNames(
                    `overflow-hidden`,
                    disabled ? `!cursor-not-allowed text-trn-300 opacity-70` : `!cursor-pointer`,
                    isFilter && selected && selected.length === 1 ? `!w-32` : ``,
                  )}
                >
                  <div className={classNames(`truncate`, !isFilter && `mt-1`)}>
                    {customLabel
                      ? customLabel
                      : selected && selected.length > 1
                      ? `${selected.length} selected`
                      : selected && selected.length === 1
                      ? selected[0].name
                      : placeholder ?? `Search`}
                  </div>
                </Tooltip>
              </ComboButton>
            )}
            <ComboOptions
              {...{
                open: isOpen,
                optionsContainerClassName,
                setPopperElement,
                floatingStyles: floatingData.floatingStyles,
                floatingProps: getFloatingProps(),
                comboDropdownInputRef,
                placeholder,
                onSearchValueChange,
                onClearSearch,
                loading,
                options,
                optionClassName,
                enableDeselectAllOption,
                deselectAllOptions,
                multiple: true,
                checkboxSide: checkboxSide,
              }}
            />
          </div>
        </Combobox>
      )}
    </>
  )
}
